import React, { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
// @ts-ignore CSS is loaded by the application bundler.
import "../../AgGridTheme.css";
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ModuleRegistry,
} from "ag-grid-community";
import {
  FaBuilding,
  FaEnvelope,
  FaGlobe,
  FaKey,
  FaLayerGroup,
  FaLink,
  FaShieldAlt,
  FaToggleOn,
  FaUser,
  FaUserTag,
} from "react-icons/fa";
import SortHeader from "../../SortingHeader";
import type { GrantedCollection, GrantedObject, RoleRef } from "../../api/entitlementsAdminApi";
import { HTTP_METHODS } from "../PermissionObject/permissionObjectUtils";
import { groupObjectsByRole, methodCellStatus } from "./accessExplorerUtils";
import AssetGroupsTable, { GroupAsset } from "./AssetGroupsTable";

ModuleRegistry.registerModules([CellStyleModule, ClientSideRowModelModule]);

export type AccessTabId = "members" | "roles" | "pages" | "apis" | "groups";

/** A role member as the Members tab shows it. */
export interface MemberRow {
  user_id: number;
  name: string;
  email: string;
  company: string;
  enabled: string | null;
}

export interface AccessLists {
  ui_pages: GrantedObject[];
  api_permissions: GrantedObject[];
  asset_groups: GrantedCollection[];
}

/** One method column: the level with ✓ when the role meets it, ✕ when it doesn't. */
export const MethodLevelCell: React.FC<{ object: GrantedObject; method: string }> = ({
  object,
  method,
}) => {
  const c = methodCellStatus(object, method);
  if (c.state === "default") {
    return (
      <span
        data-met="default"
        className="text-gray-400 italic"
        title={`Nothing stored for ${method}: the default level, ${c.level}, applies`}
      >
        {c.level}
      </span>
    );
  }
  const met = c.state === "met";
  return (
    <span
      data-met={met ? "true" : "false"}
      className={met ? "text-gray-700" : "text-gray-400"}
      title={met ? `The role level meets ${c.level}` : `Needs ${c.level}; the role level is lower`}
    >
      <span className={`font-bold ${met ? "text-green-600" : "text-red-600"}`}>{met ? "✓" : "✕"}</span>{" "}
      {c.level}
    </span>
  );
};

// Narrower side padding than the full-size admin grids: these sit in the form panel.
const headerClass = "!px-3 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer";

/** Plain header for a narrow method column (SortHeader always adds an icon and arrow). */
const MethodHeader: React.FC<{ displayName: string }> = ({ displayName }) => (
  <div className="flex items-center w-full">
    <span>{displayName}</span>
  </div>
);

const textCol = (
  field: string,
  headerName: string,
  icon: React.ReactNode,
  size: { flex?: number; minWidth?: number; width?: number } = { flex: 1, minWidth: 140 },
) => ({
  headerName,
  field,
  sortable: true,
  ...size,
  headerComponent: SortHeader,
  headerComponentParams: { displayName: headerName, enableSorting: true, icon },
  headerClass,
});

const icon = (I: React.ComponentType<{ className?: string }>) => <I className="text-gray-600" />;

const roleCol = () => textCol("via_role", "Role", icon(FaUserTag), { flex: 1, minWidth: 130 });

const cellStyle = {
  padding: "0 12px",
  fontSize: "14px",
  fontFamily: "'Poppins', sans-serif",
  color: "#374151",
};

const defaultColDef = { resizable: true, cellStyle };

const Grid: React.FC<{ rows: any[]; columnDefs: any[] }> = ({ rows, columnDefs }) => (
  <div
    className="ag-theme-alpine font-poppins text-gray-700 flex-1 min-h-[280px]"
    style={{ width: "100%", border: "1px solid #E7E4FF", borderRadius: "8px" }}
  >
    <AgGridReact
      theme="legacy"
      rowData={rows}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowHeight={44}
      headerHeight={50}
      suppressColumnVirtualisation
      domLayout="normal"
    />
  </div>
);

// In user mode rows come grouped by the role that grants them.
const byRole = (objects: GrantedObject[]) => Object.values(groupObjectsByRole(objects)).flat();

const TAB_BUTTON = "flex items-center gap-2 px-4 py-2 rounded-md whitespace-nowrap transition text-sm";

const MEMBER_COLS = [
  textCol("name", "Name", icon(FaUser), { flex: 1, minWidth: 160 }),
  textCol("email", "Email", icon(FaEnvelope), { flex: 1, minWidth: 200 }),
  textCol("company", "Company", icon(FaBuilding), { flex: 1, minWidth: 160 }),
  textCol("enabled", "Enabled", icon(FaToggleOn), { width: 130 }),
];
const ROLE_COLS = [
  textCol("role_name", "Role", icon(FaUserTag), { flex: 2, minWidth: 200 }),
  textCol("domain", "Domain", icon(FaGlobe), { flex: 1, minWidth: 130 }),
];

/**
 * One role or user, one sub-tab at a time: Members (role) or Roles (user)
 * first, then Pages, APIs and Asset groups. Each fills the rest of the panel.
 */
export const AccessTabs: React.FC<{
  access: AccessLists;
  /** User mode: a Role column on each grant. */
  showRole: boolean;
  /** Role mode: the Members tab. */
  members?: MemberRow[];
  /** User mode: the Roles tab. */
  roles?: RoleRef[];
  /** User mode, disabled user: the banner says it all, so no per-tab empty message. */
  disabled?: boolean;
  loadGroupAssets: (collectionId: number) => Promise<GroupAsset[]>;
  tab: AccessTabId;
  onTabChange: (tab: AccessTabId) => void;
}> = ({ access, showRole, members, roles, disabled, loadGroupAssets, tab, onTabChange }) => {
  const pages = useMemo(() => byRole(access.ui_pages), [access.ui_pages]);
  const apis = useMemo(() => byRole(access.api_permissions), [access.api_permissions]);
  const groups = access.asset_groups;

  const pageCols = useMemo(
    () => [
      textCol("permission_object_name", "Name", icon(FaKey), { flex: 2, minWidth: 180 }),
      textCol("po_path", "Path", icon(FaLink), { flex: 2, minWidth: 180 }),
      textCol("access_level_name", "Level", icon(FaShieldAlt), { width: 125 }),
      textCol("via_group", "Endpoint group", icon(FaLayerGroup), { flex: 1, minWidth: 160 }),
      ...(showRole ? [roleCol()] : []),
    ],
    [showRole],
  );

  const apiCols = useMemo(
    () => [
      textCol("permission_object_name", "Name", icon(FaKey), { flex: 2, minWidth: 120 }),
      textCol("po_path", "Path", icon(FaLink), { flex: 2, minWidth: 120 }),
      textCol("access_level_name", "Role level", icon(FaShieldAlt), { width: 140 }),
      ...HTTP_METHODS.map((method) => ({
        headerName: method,
        colId: method,
        width: 80,
        sortable: false,
        resizable: false,
        headerComponent: MethodHeader,
        headerComponentParams: { displayName: method },
        headerClass: "!px-2 text-gray-700",
        cellStyle: { ...cellStyle, padding: "0 8px" },
        cellRenderer: (p: any) => <MethodLevelCell object={p.data} method={method} />,
      })),
      textCol("via_group", "Endpoint group", icon(FaLayerGroup), { flex: 1, minWidth: 130 }),
      ...(showRole ? [roleCol()] : []),
    ],
    [showRole],
  );

  const memberRows = useMemo(
    () => (members ?? []).map((m) => ({ ...m, enabled: !m.enabled || m.enabled === "Y" ? "Yes" : "No" })),
    [members],
  );

  const tabs: { id: AccessTabId; label: string; count: number }[] = [
    ...(members ? [{ id: "members" as const, label: "Members", count: members.length }] : []),
    ...(roles ? [{ id: "roles" as const, label: "Roles", count: roles.length }] : []),
    { id: "pages", label: "Pages", count: pages.length },
    { id: "apis", label: "APIs", count: apis.length },
    { id: "groups", label: "Asset groups", count: groups.length },
  ];

  const current = {
    members: { rows: memberRows, cols: MEMBER_COLS, empty: "No members." },
    roles: { rows: roles ?? [], cols: ROLE_COLS, empty: "No roles." },
    pages: { rows: pages, cols: pageCols, empty: "No UI pages." },
    apis: { rows: apis, cols: apiCols, empty: "No API permissions." },
    groups: { rows: groups, cols: [], empty: "No asset groups." },
  }[tab];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex gap-3 mb-4" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => onTabChange(t.id)}
            className={`${TAB_BUTTON} ${
              tab === t.id
                ? "bg-white text-red-500 border-b-2 border-red-300 shadow"
                : "bg-gray-200 text-gray-600 hover:bg-gray-300"
            }`}
          >
            {`${t.label} (${t.count})`}
          </button>
        ))}
      </div>
      {current.rows.length === 0 ? (
        disabled ? null : (
        <p className="bg-[#f9f9f9] p-4 border rounded-lg shadow-inner text-sm text-gray-500 italic">
          {current.empty}
        </p>
        )
      ) : tab === "groups" ? (
        <AssetGroupsTable groups={groups} showRole={showRole} loadAssets={loadGroupAssets} />
      ) : (
        <Grid key={tab} rows={current.rows} columnDefs={current.cols} />
      )}
    </div>
  );
};
